import React, {
  useState,
  useEffect,
  useMemo,
  MouseEvent,
  useRef,
  Dispatch,
  SetStateAction,
  MutableRefObject,
  memo,
  ComponentProps
} from "react";
import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import { Logout } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { MediaItemDetailed, MediaResponse } from "../../types";
import { FILE_EXTENSIONS, isBrowserPlayableVideo } from "../../utilities/mediaTypes";
import { getMediaType } from "../../utilities/mediaUtils";
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import { getVideoStreamingUrl } from "../../utilities/temporaryUrlUtils";
import { embedSrc, isApiMedia, mediaSourceUrl, mediaThumbnail } from "../../utilities/mediaAsset";
import { useLogout } from "../../hooks/useLogout";
import { useMediaActions } from "../../store/media";

// Import renderers
import AudioRenderer from "./renderers/AudioRenderer";
import ImageRenderer from "./renderers/ImageRenderer";
import PDFRenderer from "./renderers/pdf-renderer/PDFRenderer";
import VideoRenderer from "./renderers/VideoRenderer";
import DocumentRenderer from "./renderers/document-renderer/DocumentRenderer";
import EmbedRenderer from "./renderers/EmbedRenderer";
import TableRenderer from "./renderers/TableRenderer";
import MediaViewerTool from "./MediaViewerTool";
import LoadingIndicator from "../common/LoadingIndicator";
import AlertDialog from "../alerts-dialog/AlertDialog";

interface MediaViewerProps {
  // Media sources (provide one of these)
  item?: MediaResponse | MediaItemDetailed | null;
  url?: string;
  file?: File;
  filename?: string;

  // Display options
  mode?: "preview" | "thumbnail" | "dialog" | "expanded";
  width?: string | number;
  height?: string | number;
  flexibleHeight?: string | number; // Variable height to control different renderer height instead hard coding

  // Dialog specific props
  open?: boolean;
  onClose?: () => void;

  // Callbacks
  onClick?: () => void;
  onLoad?: () => void;
  onError?: () => void;

  hideMediaControl?: boolean;
  onPlay?: () => void;
  onPause?: () => void;

  // When true, if the audio fails to play, fall back to showing the media's thumbnail
  // image instead of an error. Used in editor previews.
  audioThumbnailOnError?: boolean;

  controlProps?: ComponentProps<typeof MediaViewerTool>;
}

type ZoomControls = {
  zoomIn: () => void;
  zoomOut: () => void;
  resetTransform: () => void;
};

export const MediaViewer: React.FC<MediaViewerProps> = ({
  item,
  url,
  file,
  filename,
  mode = "preview",
  width = "100%",
  height = "100%",
  open,
  onClose,
  onClick,
  onLoad,
  onError,
  flexibleHeight,
  hideMediaControl = false,
  onPause,
  onPlay,
  audioThumbnailOnError = false,
  controlProps
}) => {
  const { closeMediaDetail } = useMediaActions();
  const { enqueueSnackbar } = useSnackbar();
  const { logout, isPending } = useLogout();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [streamingResult, setStreamingResult] = useState<{ mode: string; tested: boolean } | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  // When audio playback fails and audioThumbnailOnError is enabled, show the media's
  // thumbnail image instead of an error.
  const [showAudioThumbnail, setShowAudioThumbnail] = useState(false);

  // Image zoom functionality
  const zoomControlsRef = useRef<ZoomControls | null>(null); // zoom controls for normal preview
  const fullScreenZoomControlsRef = useRef<ZoomControls | null>(null); // zoom controls for full screen mode

  const [showFullScreen, setShowFullScreen] = useState(false);

  // MEMOIZED: Calculate the media URL and filename to prevent infinite re-renders
  const { mediaUrl, mediaFilename } = useMemo(() => {
    let calculatedUrl = "";
    let calculatedFilename = filename || "";

    // Priority order: url prop > file prop > item properties
    if (url) {
      // If URL is provided, use it directly - DON'T create additional blob URLs.
      // Still derive a filename (from the file/item/URL) so downstream logic that
      // depends on the extension works — e.g. isBrowserPlayableVideo() must be able
      // to detect non-browser-playable formats (AVI/MTS/VOB/MKV) to show the
      // "server processing" placeholder instead of a doomed player.
      calculatedUrl = url;
      calculatedFilename =
        calculatedFilename ||
        (file ? file.name : "") ||
        (item as any)?.original_file?.filename ||
        (calculatedUrl ? calculatedUrl.split("/").pop()?.split(/[?#]/)[0] : "") ||
        (item as any)?.name ||
        "";
    } else if (file) {
      // Only create blob URL if no explicit URL was provided
      calculatedUrl = URL.createObjectURL(file);
      calculatedFilename = calculatedFilename || file.name;
    } else if (item) {
      // Try different URL sources from the item
      calculatedUrl = mediaSourceUrl(item);

      calculatedFilename =
        calculatedFilename ||
        (item as any).original_file?.filename ||
        (calculatedUrl ? calculatedUrl.split("/").pop()?.split(/[?#]/)[0] : "") ||
        (item as any).name ||
        "";
    }
    return {
      mediaUrl: calculatedUrl,
      mediaFilename: calculatedFilename
    };
  }, [url, file, item, filename]);

  // Reset component state when media URL changes (new media item selected)
  useEffect(() => {
    if (mediaUrl) {
      setIsLoading(true);
      setLoadError(null);
      setResolvedUrl(null);
      setStreamingResult(null);
      setShowAudioThumbnail(false);
    }
  }, [mediaUrl]);

  // Cleanup blob URL when component unmounts or file changes
  // Only cleanup if we created the blob URL ourselves (when no url prop was provided)
  useEffect(() => {
    return () => {
      if (!url && file && mediaUrl && mediaUrl.startsWith("blob:")) {
        URL.revokeObjectURL(mediaUrl);
      }
    };
  }, [url, file, mediaUrl]);

  function setZoomControlsRef(ref: MutableRefObject<ZoomControls | null>, controls: ZoomControls) {
    ref.current = controls;
  }

  function handleSetZoomControl(controls: ZoomControls) {
    setZoomControlsRef(zoomControlsRef, { ...(zoomControlsRef.current || {}), ...controls });
  }
  function handleSetZoomControlFullScreen(controls: ZoomControls) {
    setZoomControlsRef(fullScreenZoomControlsRef, { ...(fullScreenZoomControlsRef.current || {}), ...controls });
  }

  // IMPROVED: Use media_type field from item if available, otherwise detect from filename/URL
  let mediaType = "unknown";
  // API media says what it is. "other" (zip, csv, docx…) still goes through the
  // MIME checks below, which tell a table from a document.
  const apiKind = isApiMedia(item) && item.media_kind !== "other" ? item.media_kind : null;
  if (apiKind) {
    mediaType = apiKind;
  } else if (item && (item as any).media_type) {
    // Convert both MIME types and Fulcrum-style types to our media type format
    const itemMediaType = (item as any).media_type.toLowerCase();

    // Embed types have their own prefix — check first since they can't collide with MIME types
    if (itemMediaType.startsWith("embed/") || itemMediaType === "embed") {
      mediaType = "embed";
    }
    // Handle MIME types (image/jpeg, video/mp4, etc.)
    else if (itemMediaType.startsWith("image/")) {
      mediaType = "image";
    } else if (itemMediaType.startsWith("video/")) {
      mediaType = "video";
    } else if (itemMediaType.startsWith("audio/")) {
      mediaType = "audio";
    } else if (itemMediaType === "application/pdf") {
      mediaType = "pdf";
    } else if (itemMediaType === "text/csv" || itemMediaType === "application/csv" || itemMediaType === "csv") {
      mediaType = "csv";
    } else if (itemMediaType.includes("document") || itemMediaType.includes("officedocument") || itemMediaType.includes("msword")) {
      mediaType = "document";
    }
    // Treat 3D model MIME types as downloadable documents unless an embed_url is provided
    else if (itemMediaType.startsWith("model/")) {
      // Some backends send bogus model/* MIME types for non-3D media — e.g. MTS video is
      // sent as "model/vnd.mts". Cross-check the file extension so real media isn't
      // misclassified as a non-previewable document (which shows a blank "Preview not
      // supported" download screen instead of the video processing placeholder / player).
      const ext = (mediaFilename || mediaUrl || "").split(/[?#]/)[0].split(".").pop()?.toLowerCase() || "";
      if (FILE_EXTENSIONS.VIDEO.includes(ext)) {
        mediaType = "video";
      } else if (FILE_EXTENSIONS.AUDIO.includes(ext)) {
        mediaType = "audio";
      } else if (FILE_EXTENSIONS.IMAGE.includes(ext)) {
        mediaType = "image";
      } else if (FILE_EXTENSIONS.PDF.includes(ext)) {
        mediaType = "pdf";
      } else {
        mediaType = "document";
      }
    }
    // Handle Fulcrum-style types (photo, video, audio, etc.)
    else if (itemMediaType === "photo") {
      mediaType = "image";
    } else if (itemMediaType === "video") {
      mediaType = "video";
    } else if (itemMediaType === "audio") {
      mediaType = "audio";
    } else if (itemMediaType === "pdf") {
      mediaType = "pdf";
    } else if (itemMediaType === "document") {
      mediaType = "document";
    }
  } else {
    // Fallback to filename/URL detection
    mediaType = getMediaType(mediaFilename || mediaUrl);
  }

  // An embed without a media_type still has an embed src.
  if (mediaType === "unknown" && item && embedSrc(item)) {
    mediaType = "embed";
  }

  const isVideo = mediaType === "video";
  const isAudio = mediaType === "audio";
  const isImage = mediaType === "image";
  const isPdf = mediaType === "pdf";
  const isEmbed = mediaType === "embed";

  // Tabular (CSV) media renders as an inline browsable table via the csv-data endpoint,
  // which is keyed by the file's S3 key. Only treat it as a table when that key is
  // available; otherwise fall back to the document/download renderer.
  const csvFileKey = (item as any)?.original_file_key ?? (item as any)?.file_key ?? null;
  const isCsv = mediaType === "csv" && Boolean(csvFileKey);
  const isDocument = mediaType === "document" || (mediaType === "csv" && !csvFileKey);

  const embedUrl = isEmbed ? embedSrc(item) : "";

  // A media record with nothing to render. An upload that never finished or failed still
  // has its row, but no display_url or url — no renderer can ever
  // resolve it, so it's a terminal "unavailable" state rather than a loading one.
  const hasNoMediaSource = !mediaUrl && !file && !embedUrl && !isCsv;

  // A video whose format the browser can't decode natively (AVI/MTS/VOB/MKV).
  const isNonBrowserPlayableVideo = Boolean(isVideo && mediaFilename && !isBrowserPlayableVideo(mediaFilename));

  // Show the "server processing" placeholder when such a video has no playable
  // streaming manifest yet: either a local file blob (upload editor) or the CMAF/HLS
  // resolution ended in fallback mode (manifest missing/403 while still transcoding).
  const shouldShowServerProcessingWarning = isNonBrowserPlayableVideo && (Boolean(file) || streamingResult?.mode === "fallback");

  // If we end up with a non-loading state but no renderer can take over (unsupported/unknown),
  // make sure we still signal "loaded" so parent overlays don't get stuck.
  useEffect(() => {
    if (isLoading) return;
    if (loadError) return;

    const hasRenderableEmbed = isEmbed && Boolean(embedUrl);
    const hasKnownRenderer = isVideo || isAudio || isImage || isPdf || isDocument || isCsv || hasRenderableEmbed;

    // These are terminal (non-loading) states that never mount a media renderer, so they
    // would otherwise never call onLoad — signal it here so parent loading overlays (e.g.
    // EntityMediaOverview's "Loading image") clear instead of spinning forever. Without
    // the hasNoMediaSource case, a failed upload (no display_url, url or thumbnail)
    // but with a known media_type leaves the overlay up, burying the edit/delete controls.
    if (shouldShowServerProcessingWarning || hasNoMediaSource || !hasKnownRenderer) {
      onLoad?.();
    }
  }, [
    isLoading,
    loadError,
    isEmbed,
    embedUrl,
    isVideo,
    isAudio,
    isImage,
    isPdf,
    isDocument,
    isCsv,
    shouldShowServerProcessingWarning,
    hasNoMediaSource,
    onLoad
  ]);

  // The stream probe looks for the manifest next to the original file, so API media are
  // probed from their original (`url`), whatever the viewer is shown. Once the stream
  // report ships, `display_url` is the manifest itself, and probing it would find nothing.
  const videoProbeUrl = isApiMedia(item) && (item as any).url ? (item as any).url : mediaUrl;

  // URL resolution for videos (using temporaryUrlUtils for complex streaming logic)
  useEffect(() => {
    // Embeds don't need URL resolution — they use their embed src directly
    if (isEmbed) {
      setIsLoading(false);
      setResolvedUrl(null);
      return;
    }

    if (!mediaUrl) {
      setIsLoading(false);
      setResolvedUrl(null);
      return;
    }

    if (isVideo) {
      setIsLoading(true);
      getVideoStreamingUrl(videoProbeUrl)
        .then((result) => {
          setResolvedUrl(result.url);
          setStreamingResult({ mode: result.mode, tested: result.tested });
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("[MediaViewer] Video URL resolution failed:", error);
          setResolvedUrl(mediaUrl);
          setStreamingResult({ mode: "fallback", tested: false });
          setIsLoading(false);
        });
    } else {
      // For non-video media, use URL directly
      setResolvedUrl(mediaUrl);
      setIsLoading(false);
    }
  }, [mediaUrl, videoProbeUrl, isVideo, isEmbed]);

  // Handle load/error for media
  const handleLoad = () => {
    setIsLoading(false);
    setLoadError(null); // Clear any previous errors on successful load
    if (onLoad) onLoad();
  };

  function handleCloseLoginPromptModal() {
    if (isPending) return;

    setShowLoginPrompt(false);
    setIsLoading(false);
  }

  async function handleLogout() {
    try {
      await logout();
    } catch (err: any) {
      const errMsg = err?.message;
      enqueueUniqueSnackbar(enqueueSnackbar, `Failed to login. Error: ${errMsg}`);
    }
  }

  const handleError = async (error?: any) => {
    setIsLoading(false);

    // In editor previews, gracefully fall back to the media's thumbnail image instead of
    // showing an error when audio can't be played. The `!showAudioThumbnail` guard avoids
    // a loop if the thumbnail image itself fails to load (then the real error is shown).
    if (audioThumbnailOnError && isAudio && !showAudioThumbnail) {
      const thumb = mediaThumbnail(item);
      if (thumb) {
        setShowAudioThumbnail(true);
        setLoadError(null);
        if (onLoad) onLoad();
        return;
      }
    }

    // If the resource failed to load and it's not a local blob/data URL,
    // show the login-prompt dialog (session may have expired).
    if (error && mediaUrl && !mediaUrl.startsWith("blob:") && !mediaUrl.startsWith("data:")) {
      setShowLoginPrompt(true);
    }

    // Provide more specific error messages based on media type
    let errorMessage = "Failed to load media";

    if (isVideo && error) {
      errorMessage = "Unable to load video file. CMAF streaming may not be available for this video.";
    } else if (isAudio && error) {
      errorMessage = "Unable to load audio file. Please try again or contact support if the issue persists.";
    } else if (isPdf && error) {
      if (error.message && error.message.includes("403")) {
        errorMessage = "PDF access denied. This file may require authentication.";
      } else if (error.message && error.message.includes("CORS")) {
        errorMessage = "PDF blocked by CORS policy.";
      } else {
        errorMessage = "Unable to load PDF file. Please try again or contact support if the issue persists.";
      }
    } else if (isDocument && error) {
      errorMessage = "Unable to load document file. Please try again or contact support if the issue persists.";
    } else if (isImage && error) {
      // Enhanced error handling for Fulcrum photos
      if (error.message && (error.message.includes("external data source") || error.message.includes("API key"))) {
        errorMessage = error.message; // Use the detailed error from useFulcrumPhoto
      } else if (error.message && error.message.includes("403")) {
        errorMessage = "Image access denied. This photo may require authentication.";
      } else if (error.message && error.message.includes("NetworkError")) {
        errorMessage = "Network error loading image. Please check your connection and try again.";
      } else {
        errorMessage = "Unable to load image file. Please try again or contact support if the issue persists.";
      }
    } else if (error) {
      errorMessage = "Unable to load document file. Please try again or contact support if the issue persists.";
    }

    console.error("[MediaViewer] Media error:", error);
    setLoadError(errorMessage);

    // Show snackbar for all media errors
    enqueueUniqueSnackbar(enqueueSnackbar, errorMessage, {
      variant: "error",
      autoHideDuration: 5000
    });

    if (onError) onError();
  };

  // Render the appropriate media renderer
  const renderMediaContent = ({
    viewMode,
    setZoomControls,
    height,
    width,
    isInFullscreenDialog = false
  }: {
    viewMode?: MediaViewerProps["mode"];
    setZoomControls: (obj: ZoomControls) => void | Dispatch<SetStateAction<ZoomControls>>;
    height?: string | number;
    width?: string | number;
    isInFullscreenDialog?: boolean;
  }) => {
    // Show loading state if loading
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "200px",
            gap: 2
          }}
        >
          <CircularProgress />
          <Typography variant='body2' color='text.secondary'>
            {isVideo
              ? "Loading video..."
              : isAudio
                ? "Loading audio..."
                : isPdf
                  ? "Loading PDF..."
                  : isDocument
                    ? "Loading Document..."
                    : isImage
                      ? "Loading image..."
                      : isEmbed
                        ? "Loading embedded content..."
                        : "Loading media..."}
          </Typography>
        </Box>
      );
    }

    if (showLoginPrompt) {
      return (
        <AlertDialog open variant='error' onClose={handleCloseLoginPromptModal}>
          <Stack direction={"row"} px={24} gap={16}>
            <AlertDialog.Icon />
            <AlertDialog.Title sx={{ pl: 0 }}>Resource unavailable</AlertDialog.Title>
          </Stack>
          <AlertDialog.Content>
            <Typography variant='h6' color='grey.600'>
              We couldn't load the requested resource. This can happen if your session has expired, the resource has been deleted, or you
              don't have permission to access it. Logging out and signing back in sometimes resolves this. If the problem persists, contact
              your administrator.
            </Typography>
          </AlertDialog.Content>
          <AlertDialog.Actions>
            <Button type='button' variant='contained' onClick={() => setShowLoginPrompt(false)} disableElevation>
              Cancel
            </Button>
            <Button
              disableElevation
              type='button'
              startIcon={!isPending && <Logout />}
              variant='secondary'
              disabled={isPending}
              onClick={handleLogout}
              sx={{ width: "6rem" }}
            >
              {isPending ? <CircularProgress size={24} /> : "Logout"}
            </Button>
          </AlertDialog.Actions>
        </AlertDialog>
      );
    }

    if (loadError) {
      return (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", px: "1rem" }}>
            <Typography color='error'>{loadError}</Typography>
          </Box>
        </>
      );
    }

    // Embeds don't need a resolvedUrl — render directly
    if (isEmbed && embedUrl) {
      // When inside the fullscreen Dialog, build an explicit close handler that
      // first dismisses the Dialog (so MUI's Portal tears down cleanly), then
      // closes the media preview via onClose prop or the store action.
      const embedOnClose = isInFullscreenDialog
        ? () => {
            setShowFullScreen(false);
            if (onClose) onClose();
            else closeMediaDetail();
          }
        : onClose;

      return (
        <EmbedRenderer
          embedUrl={embedUrl}
          title={(item as any)?.name}
          mode={viewMode || mode}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
          onClose={embedOnClose}
          onFullscreen={isInFullscreenDialog ? () => setShowFullScreen(false) : () => setShowFullScreen(true)}
          isFullscreen={isInFullscreenDialog}
        />
      );
    }

    // Tabular (CSV) media renders as an inline browsable table, driven by its file key
    // rather than the URL pipeline — so handle it before the "no URL" guard below.
    if (isCsv) {
      return (
        <TableRenderer
          fileKey={csvFileKey}
          filename={mediaFilename}
          mode={viewMode || mode}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
        />
      );
    }

    // No URL, file, embed or file key — the stored file is missing (typically an upload
    // that failed server-side). Show a broken-media state rather than a doomed renderer,
    // so the user can see what happened and delete the item.
    if (hasNoMediaSource) {
      const isCompact = (viewMode || mode) === "thumbnail";
      const mediaLabel = isImage ? "image" : isVideo ? "video" : isAudio ? "audio file" : isPdf ? "PDF" : isDocument ? "document" : "file";

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            gap: isCompact ? 0.5 : 1,
            px: "1.5rem",
            textAlign: "center"
          }}
        >
          <BrokenImageOutlinedIcon sx={{ fontSize: isCompact ? "2rem" : "3rem", color: "grey.400" }} />
          <Typography variant={isCompact ? "body2" : "h5"} color='grey.100'>
            This {mediaLabel} could not be loaded
          </Typography>
          {!isCompact && (
            <Typography variant='body2' color='grey.300'>
              The upload didn't complete, so no file is stored for {mediaFilename ? `"${mediaFilename}"` : "this item"}. It can safely be
              deleted.
            </Typography>
          )}
        </Box>
      );
    }

    // Show server processing warning when the video format can't be played natively
    // by the browser (e.g. AVI, MTS, VOB, MKV). `shouldShowServerProcessingWarning` is
    // computed at component scope so the "signal loaded" effect can also react to it and
    // clear parent loading overlays (this placeholder never mounts a media renderer).
    if (shouldShowServerProcessingWarning) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            padding: 2,
            textAlign: "center"
          }}
        >
          <Typography variant='body1' color='warning.main' gutterBottom>
            📹 Server Processing Required
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            This format file will need to be processed on the server to play it
          </Typography>
        </Box>
      );
    }

    // Handle unknown media types
    if (!isVideo && !isAudio && !isImage && !isPdf && !isDocument && !isEmbed && !resolvedUrl) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <Typography color='error'>Unsupported media type: {mediaType || "unknown"}</Typography>
        </Box>
      );
    }

    if (isVideo && !isLoading && (resolvedUrl || file)) {
      // IMPROVED: Only render video if we're not loading and have a valid URL or file
      return (
        <VideoRenderer
          url={resolvedUrl || undefined}
          file={file}
          filename={mediaFilename}
          mode={mode}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
          onPlay={onPlay}
          onPause={onPause}
        />
      );
    }

    if (isAudio && !isLoading && (resolvedUrl || file)) {
      // In editor previews, if audio playback failed we show the thumbnail image instead.
      if (showAudioThumbnail) {
        const thumbUrl = mediaThumbnail(item);
        if (thumbUrl) {
          return (
            <Box
              sx={{
                width,
                height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                cursor: onClick ? "pointer" : "default"
              }}
              onClick={onClick}
            >
              <img
                src={thumbUrl}
                alt={mediaFilename || "Audio"}
                onError={handleError}
                style={{
                  height: "100%",
                  width: "auto",
                  maxWidth: "100%",
                  objectFit: "contain",
                  display: "block"
                }}
              />
            </Box>
          );
        }
      }
      return (
        <AudioRenderer
          url={resolvedUrl || undefined}
          file={file}
          filename={mediaFilename}
          mode={mode}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
        />
      );
    }

    if (isPdf && !isLoading && (resolvedUrl || file)) {
      return (
        <PDFRenderer
          url={resolvedUrl || undefined}
          file={file}
          filename={mediaFilename}
          mode={mode}
          width={width}
          height={height}
          flexibleHeight={flexibleHeight}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
        />
      );
    }

    if (isDocument && !isLoading && (resolvedUrl || file)) {
      return (
        <DocumentRenderer
          url={resolvedUrl || undefined}
          file={file}
          filename={mediaFilename}
          mode={mode}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
        />
      );
    }

    if (isImage && !isLoading && (resolvedUrl || file)) {
      return (
        <ImageRenderer
          url={resolvedUrl || undefined}
          file={file}
          filename={mediaFilename}
          mode={viewMode || mode}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
          onZoomControlsChange={setZoomControls}
        />
      );
    }

    // Fallback for when no media is available
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <Typography color='grey.200'>No media available</Typography>
      </Box>
    );
  };

  // Dialog mode - render in modal
  if (mode === "dialog") {
    // FIXED: Always show dialog when open=true, regardless of content readiness
    // This allows us to show loading states and error messages properly
    return (
      <Dialog
        open={Boolean(open)}
        onClose={onClose}
        maxWidth={false}
        fullWidth={false}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.88)" // Increased backdrop opacity for better focus
            }
          }
        }}
        PaperProps={{
          sx: {
            bgcolor: "rgba(18, 18, 18, 1)", // Slightly increased paper opacity
            // IMPROVED: Only use video-specific sizing when video is actually ready to render
            maxWidth: isVideo && !isLoading && resolvedUrl ? "95vw" : "90vw",
            minWidth: isVideo && !isLoading && resolvedUrl ? "80vw" : "600px",
            // Give the dialog an explicit height when showing a video so that
            // Video.js fill mode has a real container to stretch into.
            height: isVideo && !isLoading && resolvedUrl ? "90vh" : "auto",
            maxHeight: "90vh",
            // IMPROVED: Ensure minimum height during loading to prevent layout shifts
            minHeight: isLoading ? "200px" : "auto"
          }
        }}
        TransitionProps={{
          timeout: 300
        }}
      >
        {/* Floating overlay controls */}
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1000,
            display: "flex",
            gap: 1,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(12px)",
            borderRadius: 3,
            padding: 1,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.4)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            "& .MuiIconButton-root": {
              color: "white",
              borderRadius: 2,
              transition: "all 0.2s ease-out",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
                transform: "scale(1.1)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)"
              }
            }
          }}
        >
          {isImage && zoomControlsRef.current && (
            <>
              <IconButton onClick={zoomControlsRef?.current?.zoomOut} size='small'>
                <ZoomOutIcon />
              </IconButton>
              <IconButton onClick={zoomControlsRef?.current?.resetTransform} size='small'>
                <RestartAltIcon />
              </IconButton>
              <IconButton onClick={zoomControlsRef?.current?.zoomIn} size='small'>
                <ZoomInIcon />
              </IconButton>
            </>
          )}
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            p: 0,
            // For video in fill mode, height:100% lets the player stretch to
            // the full dialog paper height. For images/other, keep auto sizing.
            height: isVideo ? "100%" : isImage ? "80vh" : "auto",
            minHeight: isVideo ? "0" : "300px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          {renderMediaContent({ setZoomControls: handleSetZoomControl })}
        </DialogContent>
      </Dialog>
    );
  }

  function handleCloseFullScreen(_e: MouseEvent<HTMLButtonElement>, reason: string) {
    if (reason === "backdropFilter") return;
    setShowFullScreen(false);
  }

  // Preview/thumbnail mode - direct render
  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: width,
          height: isVideo || showAudioThumbnail ? "100%" : height,
          minHeight: isVideo ? "auto" : height,
          maxHeight: isPdf ? "100%" : height,
          display: "flex",
          // PDFs can be taller than the container and scroll. Centering vertically would
          // clip the top of the document and make it unreachable, so align to the top.
          alignItems: isPdf ? "flex-start" : "center",
          justifyContent: "center",
          bgcolor: "#475467",
          borderTop: "1px solid",
          borderColor: "#5c636e",
          overflow: isPdf ? "auto" : "hidden",
          paddingBottom: isPdf ? "8.5rem" : 0
        }}
        className='scroller'
      >
        {renderMediaContent({ setZoomControls: handleSetZoomControl, height: isPdf || showAudioThumbnail ? "100%" : height, width })}

        {!isVideo && !isEmbed && !hideMediaControl && !hasNoMediaSource && (
          <MediaViewerTool
            onFullScreen={() => setShowFullScreen(true)}
            hideZoomControls={!isImage}
            onZoomIn={() => zoomControlsRef?.current?.zoomIn()}
            onZoomOut={() => zoomControlsRef?.current?.zoomOut()}
            {...controlProps}
            sx={{ ...(!isVideo && { bottom: "9rem", right: "1rem" }), ...controlProps?.sx }}
          />
        )}
      </Box>

      <Dialog
        fullScreen
        open={showFullScreen}
        PaperProps={{
          sx: {
            background: "#0e0d0de0",
            backdropFilter: "blur(0.2rem)"
          }
        }}
        disableEscapeKeyDown={false}
        onClose={handleCloseFullScreen}
      >
        <DialogContent
          sx={{
            position: "relative",
            padding: 0,
            overflow: "hidden",
            "& .close-fullscreen": {
              top: "-3rem",
              visibility: "hidden"
            },
            "&:hover .close-fullscreen": {
              top: "1rem",
              visibility: "visible"
            },
            "& .media-controls": {
              opacity: 0
            },
            "&:hover .media-controls": {
              opacity: 1
            }
          }}
        >
          {renderMediaContent({
            setZoomControls: handleSetZoomControlFullScreen,
            viewMode: "expanded",
            height: "100%",
            width,
            isInFullscreenDialog: true
          })}

          {isLoading && (
            <LoadingIndicator
              message={`Loading ${mediaType !== "unknown" ? mediaType : "Media"}`}
              loaderSize={32}
              sx={{
                position: "absolute",
                height: "100%",
                width: "100%",
                top: 0,
                left: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
                bgcolor: "#172b4973",
                backdropFilter: "blur(8px)"
              }}
            />
          )}

          {!isVideo && !isEmbed && (
            <MediaViewerTool
              sx={{ left: "50%", right: "none", transform: "translateX(-50%)", zIndex: 2 }}
              className='media-controls'
              exitFullScreen={() => setShowFullScreen(false)}
              onZoomIn={() => fullScreenZoomControlsRef.current?.zoomIn()}
              onZoomOut={() => fullScreenZoomControlsRef.current?.zoomOut()}
              hideZoomControls={!isImage}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(MediaViewer, (prev, next) => {
  // MediaViewer is used for many media types; for embeds the `url` prop may be empty or unchanged
  // while `item.id` / the embed src changes (carousel navigation). Only comparing `url` causes stale renders.
  const prevItem: any = prev.item;
  const nextItem: any = next.item;

  const prevEmbed = embedSrc(prevItem);
  const nextEmbed = embedSrc(nextItem);

  return (
    prev.url === next.url &&
    prev.file === next.file &&
    prev.filename === next.filename &&
    prevItem?.id === nextItem?.id &&
    prevEmbed === nextEmbed
  );
});
